import React, { useCallback, useMemo, useState } from "react";
import { connect } from "react-redux";

import { Col, Row } from "antd/es/grid";
import Highcharts, { SeriesClickEventObject } from "highcharts";
import HighchartsReact from "highcharts-react-official";
import { useIntl } from "react-intl";

import Color from "@mapmycustomers/shared/enum/Color";
import Funnel from "@mapmycustomers/shared/types/entity/deals/Funnel";

import { RootState } from "@app/store/rootReducer";
import allColors from "@app/styles/_colors.module.scss";
import layout from "@app/styles/layout";
import colorNameToHex from "@app/util/colors/colorNameToHex";
import getSemiTransparentColor from "@app/util/colors/getSemiTransparentColor";

import messages from "../../messages";
import { selectFunnelId } from "../../store/actions";
import { getFunnelData, getFunnelId } from "../../store/selectors";
import { FunnelDataRecord } from "../../type/DataRecord";
import { COLUMN_BAR_HEIGHT } from "../../util/const";
import getRandomColor from "../../util/getRandomColor";
import useChartConfig from "../../util/useChartConfig";
import useGrossValueFormatter from "../../util/useGrossValueFormatter";

import styles from "./Funnels.module.scss";

interface Props {
  funnelData: FunnelDataRecord[];
  funnelId?: Funnel["id"];
  selectFunnelId: typeof selectFunnelId;
}

const Funnels: React.FC<Props> = ({ funnelData, funnelId, selectFunnelId }) => {
  const intl = useIntl();

  const [funnelDataRecord, setFunnelDataRecord] = useState<FunnelDataRecord | undefined>(
    funnelId ? funnelData.find(({ funnel: { id } }) => funnelId === id) : undefined
  );
  const funnelChartData = useMemo(
    () =>
      funnelData.map(({ funnel, value }) => ({
        custom: { funnel },
        name: funnel.name,
        y: value,
      })),
    [funnelData]
  );

  const colors = useMemo(
    () =>
      funnelData.map(({ funnel: { id } }) =>
        getSemiTransparentColor(getRandomColor(id), funnelDataRecord?.funnel.id === id ? 0 : 0.8)
      ),
    [funnelData, funnelDataRecord]
  );

  const valueFormatter = useGrossValueFormatter(intl);

  const handleBarClick = useCallback(
    (event: SeriesClickEventObject) => {
      const funnel: Funnel = event.point.options.custom?.funnel;
      setFunnelDataRecord(funnelData.find(({ funnel: { id } }) => funnel.id === id));
      selectFunnelId(funnel.id);
    },
    [funnelData, setFunnelDataRecord, selectFunnelId]
  );

  const selectedPosition = useMemo(() => {
    return funnelId ? funnelData.findIndex(({ funnel: { id } }) => id === funnelId) : undefined;
  }, [funnelData, funnelId]);

  const chartConfig = useChartConfig({
    colors,
    columnChartHeight: COLUMN_BAR_HEIGHT * funnelChartData.length,
    intl,
    onBarClick: handleBarClick,
    selectedPosition,
    seriesData: funnelChartData,
    seriesName: intl.formatMessage(messages.funnels),
    usePie: false,
  });

  return (
    <div className={styles.chartContainer}>
      <HighchartsReact highcharts={Highcharts} isPureConfig options={chartConfig} />
      {funnelDataRecord && (
        <Row align="middle" className={styles.infoBlock} justify="space-between">
          <Col>{funnelDataRecord.funnel.name}</Col>
          <Col className={styles.funnelValue}>{funnelDataRecord.value}</Col>
        </Row>
      )}
      {funnelDataRecord?.stages.map(({ entity, value }) => (
        <Row className={styles.stageData} justify="space-between" key={entity.id}>
          <Col span={22}>
            <Row align="middle" gutter={layout.spacerS}>
              <Col span={3}>
                <div
                  className={styles.stageColor}
                  style={{
                    backgroundColor: entity.color
                      ? colorNameToHex(entity.color as Color)
                      : allColors.white,
                  }}
                />
              </Col>
              <Col className={styles.stageName} span={20}>
                {entity.name}
              </Col>
            </Row>
          </Col>
          <Col span={1}>
            <Row justify="end">
              <Col>{valueFormatter(value)}</Col>
            </Row>
          </Col>
        </Row>
      ))}
    </div>
  );
};

const mapStateToProps = (state: RootState) => ({
  funnelData: getFunnelData(state),
  funnelId: getFunnelId(state),
});

const mapDispatchToProps = {
  selectFunnelId,
};

export default connect(mapStateToProps, mapDispatchToProps)(Funnels);
